import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Mic, Pencil, ImageUp, Scissors, Wand2 } from "lucide-react";

const marqueeWords = ["couture", "shape", "silhouette", "drape", "warp & weft", "atelier", "bias-cut", "sculpt", "hemline", "hand-stitched"];

export default function Landing() {
  const { user, login } = useAuth();

  return (
    <div className="bg-zinc-50 text-zinc-950 min-h-screen relative overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 min-h-[95vh] flex flex-col justify-between grain">
        <div className="max-w-[1600px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative z-10">
          <div className="lg:col-span-7 reveal">
            <div className="overline text-zinc-500 mb-6" data-testid="landing-overline">Issue N°01 — Feb 2026 · The AI Couture Journal</div>
            <h1 className="font-serif text-6xl md:text-8xl lg:text-[9rem] leading-[0.85] tracking-tight" data-testid="hero-headline">
              Design the <em className="italic font-light">clothes</em>
              <br />you couldn&apos;t <em className="italic font-light">draw</em>.
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-zinc-700">
              Not another preset library. Sketch a silhouette by hand, speak the fabric aloud, drop in a mood photo — Insta<span className="italic font-serif">Lux</span> renders the finished piece in seconds. Shape, length, texture, drape. Yours to command.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              {user ? (
                <Link
                  to="/studio"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-zinc-950 text-zinc-50 rounded-full overline hover:bg-lime-400 hover:text-zinc-950 transition-colors"
                  data-testid="hero-open-studio-btn"
                >
                  Open the Studio <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : (
                <button
                  onClick={login}
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-lime-400 text-zinc-950 rounded-full overline hover:bg-zinc-950 hover:text-lime-400 transition-colors"
                  data-testid="hero-cta-btn"
                >
                  Enter InstaLux <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
              <a href="#how" className="overline text-zinc-500 hover:text-zinc-950 transition-colors">How it works ↓</a>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-8 relative">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1733322992706-1210ca79f4df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxoaWdoJTIwZmFzaGlvbiUyMHJ1bndheXxlbnwwfHx8fDE3ODc1NDM3NjN8MA&ixlib=rb-4.1.0&q=85"
                alt="runway"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-zinc-950/40" />
              <div className="absolute bottom-4 left-4 right-4 text-zinc-50">
                <div className="overline opacity-70">Editorial N°04</div>
                <div className="font-serif text-2xl italic">Rendered from a single voice memo</div>
              </div>
            </div>
            <div className="absolute -top-4 -left-4 md:-left-12 hidden md:block backdrop-blur-xl bg-white/70 border border-zinc-200/60 px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.12)] rotate-[-2deg]">
              <div className="overline text-zinc-500">Prompt · voice</div>
              <div className="font-serif italic text-xl">&ldquo;a bias-cut satin slip, cinched at the ankle, no straps.&rdquo;</div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="mt-16 relative z-10 border-y border-zinc-950/10 py-4 overflow-hidden">
          <div className="flex gap-12 marquee-track whitespace-nowrap font-serif italic text-4xl md:text-5xl">
            {[...marqueeWords, ...marqueeWords, ...marqueeWords].map((w, i) => (
              <span key={`marq-${i}-${w}`} className="text-zinc-950/80">— {w}</span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="px-6 md:px-12 py-24 border-t border-zinc-200">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <div className="overline text-zinc-500 mb-3">§ 01 — The Method</div>
              <h2 className="font-serif text-5xl md:text-6xl leading-[0.9]">Four ways in.<br /><em className="italic font-light">One garment out.</em></h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 pt-6 text-lg leading-relaxed text-zinc-700">
              Every input feeds the same engine. Combine them — sketch a shape, speak the mood, upload a scanned drawing, tap a fabric. InstaLux mixes them into a single instruction and returns a photoreal render.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-zinc-200">
            {[
              { icon: Pencil, title: "Sketch", body: "Draw silhouettes on a pressure-aware canvas. Erase, undo, sculpt." },
              { icon: Mic, title: "Speak", body: "Voice-describe the fabric, cut, mood. Whisper turns it into text." },
              { icon: ImageUp, title: "Upload", body: "Attach photos of a physical sketch, mood board, or reference." },
              { icon: Scissors, title: "Tailor", body: "Pick fabric, length, silhouette, texture from a curated library." },
            ].map((s, i) => (
              <div key={s.title} className="border-r last:border-r-0 border-b md:border-b-0 border-zinc-200 p-8 hover:bg-lime-400/10 transition-colors group">
                <div className="overline text-zinc-400 mb-4">0{i+1}</div>
                <s.icon className="w-8 h-8 mb-6 text-zinc-950 group-hover:text-lime-500 transition-colors" strokeWidth={1.5} />
                <div className="font-serif text-3xl mb-2">{s.title}</div>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="px-6 md:px-12 py-24 bg-zinc-950 text-zinc-50 relative grain">
        <div className="max-w-[1600px] mx-auto relative z-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline text-lime-400 mb-3">§ 02 — Portfolio</div>
              <h2 className="font-serif text-5xl md:text-7xl leading-[0.9]">Editorials from<br /><em className="italic font-light">the machine.</em></h2>
            </div>
            {user && (
              <Link to="/gallery" className="hidden md:inline-flex overline items-center gap-2 hover:text-lime-400 transition-colors">
                Your archive <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            {[
              "https://images.pexels.com/photos/1655841/pexels-photo-1655841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800",
              "https://images.unsplash.com/photo-1586447795212-b8ea5253ac2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwc2tldGNoJTIwaWxsdXN0cmF0aW9ufGVufDB8fHx8MTc4NzU0Mzc2M3ww&ixlib=rb-4.1.0&q=85",
              "https://images.unsplash.com/photo-1583744946564-b52ac1c389c8?w=800",
              "https://images.pexels.com/photos/7283181/pexels-photo-7283181.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800",
            ].map((src, i) => (
              <div key={src} className={`relative overflow-hidden ${i % 2 === 0 ? "aspect-[3/4]" : "aspect-[4/5]"} group`}>
                <img src={src} alt="editorial" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 to-transparent" />
                <div className="absolute bottom-3 left-3 overline text-zinc-100">Look N°0{i+1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-24 border-t border-zinc-200">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.9]">Your ideas deserve<br /><em className="italic font-light">a first cut.</em></h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            {user ? (
              <Link to="/studio" className="inline-flex items-center gap-3 px-8 py-4 bg-zinc-950 text-lime-400 rounded-full overline hover:bg-lime-400 hover:text-zinc-950 transition-colors" data-testid="cta-studio-btn">
                <Wand2 className="w-4 h-4" /> Enter the Studio
              </Link>
            ) : (
              <button onClick={login} className="inline-flex items-center gap-3 px-8 py-4 bg-zinc-950 text-lime-400 rounded-full overline hover:bg-lime-400 hover:text-zinc-950 transition-colors" data-testid="cta-login-btn">
                <Wand2 className="w-4 h-4" /> Begin — it&apos;s free
              </button>
            )}
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-12 py-10 border-t border-zinc-200 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
        <div className="overline">InstaLux © 2026 — Rendered in the machine</div>
        <div className="flex items-center gap-6">
          <Link to="/apply-maker" className="overline hover:text-zinc-950 transition-colors" data-testid="landing-apply-link">Are you a maker? Apply →</Link>
          <span className="overline">Powered by Nano Banana · Whisper · Editorial spirit</span>
        </div>
      </footer>
    </div>
  );
}
